import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Loader2, Link2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Alert, AlertDescription } from '../ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  chargeMapAdminService,
  CANONICAL_SECTIONS,
  DEMAND_UNITS,
  type ExpectedChargeItemRow,
  type ExpectedItemUsage,
} from '../../services/chargeMapAdminService';
import {
  itemNameWithUnit, splitItemUnit, type DemandUnit,
} from '../../../../../features/billValidation/utils/expectedItemUnit';

const FORM_SELECT_CLASS =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

type FormState = { base: string; unit: DemandUnit | ''; section: string; validation_possible: boolean };

const EMPTY: FormState = { base: '', unit: '', section: 'distribution', validation_possible: true };

type ExpectedChargeItemsTabProps = {
  /** Opens the Mappings tab filtered to this item. */
  onShowMappings: (itemName: string) => void;
};

/**
 * Expected Charge Items: the names a retailer's bill line is mapped to.
 *
 * A demand item exists once per unit, named the way the network tariff names
 * the expected line ("Peak Demand - kW", "Peak Demand - Annual kVA"), so a
 * mapping to it pairs with that line by name. The unit is picked here and the
 * name follows (migration 134).
 *
 * An item with mappings cannot be deleted: the delete shows those mappings
 * instead, so the admin can repoint them first.
 */
export const ExpectedChargeItemsTab: React.FC<ExpectedChargeItemsTabProps> = ({ onShowMappings }) => {
  const [rows, setRows] = useState<ExpectedChargeItemRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<ExpectedChargeItemRow | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // The dependency list: opened by "Used by", or by a delete that is refused.
  const [usagesFor, setUsagesFor] = useState<ExpectedChargeItemRow | null>(null);
  const [usages, setUsages] = useState<ExpectedItemUsage[]>([]);
  const [usagesLoading, setUsagesLoading] = useState(false);
  const [usagesError, setUsagesError] = useState<string | null>(null);

  const [deleting, setDeleting] = useState<ExpectedChargeItemRow | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await chargeMapAdminService.vocabulary.list();
    if (res.success) { setRows(res.data ?? []); setError(null); }
    else setError(res.error ?? 'Failed to load expected charge items');
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (row: ExpectedChargeItemRow) => {
    setEditing(row);
    setForm({
      base: row.unit ? splitItemUnit(row.name).base : row.name,
      unit: row.unit ?? '',
      section: row.section,
      validation_possible: row.validation_possible,
    });
    setFormError(null);
    setDialogOpen(true);
  };

  const previewName = form.base.trim() ? itemNameWithUnit(form.base, form.unit || null) : '';

  const handleSave = async () => {
    if (!form.base.trim()) { setFormError('Name is required'); return; }
    setSaving(true);
    setFormError(null);
    const body = {
      name: form.base.trim(),
      unit: form.unit || null,
      section: form.section as ExpectedChargeItemRow['section'],
      validation_possible: form.validation_possible,
    };
    const res = editing
      ? await chargeMapAdminService.vocabulary.update(editing.id, body)
      : await chargeMapAdminService.vocabulary.create(body);
    setSaving(false);
    if (!res.success) { setFormError(res.error ?? 'Save failed'); return; }
    setDialogOpen(false);
    await load();
  };

  const showUsages = async (row: ExpectedChargeItemRow, known?: ExpectedItemUsage[]) => {
    setUsagesFor(row);
    setUsagesError(null);
    if (known) { setUsages(known); return; }
    setUsages([]);
    setUsagesLoading(true);
    const res = await chargeMapAdminService.vocabulary.usages(row.id);
    setUsagesLoading(false);
    if (res.success) setUsages(res.data ?? []);
    else setUsagesError(res.error ?? 'Could not load the mappings');
  };

  const askDelete = (row: ExpectedChargeItemRow) => {
    // Known to be in use: show what depends on it rather than offering a
    // delete that will be refused.
    if ((row.usage_count ?? 0) > 0) { void showUsages(row); return; }
    setDeleting(row);
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    const res = await chargeMapAdminService.vocabulary.remove(deleting.id);
    setDeleteBusy(false);
    const row = deleting;
    setDeleting(null);
    if (!res.success) {
      // Mapped since the list loaded: the server refuses with the mappings.
      if (res.data && res.data.length > 0) { void showUsages(row, res.data); return; }
      setError(res.error ?? 'Delete failed');
      return;
    }
    await load();
  };

  const q = filter.trim().toLowerCase();
  const visible = q ? rows.filter((r) => r.name.toLowerCase().includes(q) || r.section.includes(q)) : rows;

  if (loading) {
    return <div className="flex justify-center p-8"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Filter by name or section…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="max-w-xs"
        />
        <span className="text-sm text-muted-foreground">{visible.length} of {rows.length} items</span>
        <div className="flex-1" />
        <Button size="sm" onClick={openNew}><Plus className="h-4 w-4 mr-1" />Add item</Button>
      </div>

      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead>Section</TableHead>
              <TableHead>Validation possible</TableHead>
              <TableHead>Used by</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visible.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-6">No items.</TableCell>
              </TableRow>
            )}
            {visible.map((row) => {
              const used = row.usage_count ?? 0;
              return (
                <TableRow key={row.id}>
                  <TableCell className="font-medium">{row.name}</TableCell>
                  <TableCell className="text-sm">{row.unit ?? '—'}</TableCell>
                  <TableCell className="text-sm">{row.section}</TableCell>
                  <TableCell className="text-sm">{row.validation_possible ? 'Yes' : 'No'}</TableCell>
                  <TableCell>
                    {used > 0 ? (
                      <Button variant="link" size="sm" className="h-auto p-0" onClick={() => void showUsages(row)}>
                        {used} mapping{used === 1 ? '' : 's'}
                      </Button>
                    ) : <span className="text-sm text-muted-foreground">Not used</span>}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(row)} aria-label="Edit">
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost" size="icon" onClick={() => askDelete(row)}
                        aria-label={used > 0 ? 'In use — show mappings' : 'Delete'}
                        title={used > 0 ? `Used by ${used} mapping${used === 1 ? '' : 's'} — cannot be deleted` : 'Delete'}
                      >
                        <Trash2 className={`h-3.5 w-3.5 ${used > 0 ? 'text-muted-foreground' : 'text-destructive'}`} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Add / edit */}
      <Dialog open={dialogOpen} onOpenChange={(v) => { if (!v && !saving) setDialogOpen(false); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit expected charge item' : 'New expected charge item'}</DialogTitle>
            <DialogDescription>
              A demand charge is one item per unit. Pick the unit and the name is shown with it, the way the network
              tariff names the expected line, so a mapping to it pairs exactly.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="eci-name">Name <span className="text-destructive">*</span></Label>
              <Input id="eci-name" value={form.base} onChange={(e) => setForm((f) => ({ ...f, base: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="eci-unit">Unit</Label>
              <select
                id="eci-unit"
                value={form.unit}
                onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value as FormState['unit'] }))}
                className={FORM_SELECT_CLASS}
              >
                <option value="">Not a demand charge</option>
                {DEMAND_UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
              {previewName && <p className="text-xs text-muted-foreground">Saved as <span className="font-medium">{previewName}</span></p>}
            </div>
            <div className="space-y-1">
              <Label htmlFor="eci-section">Section <span className="text-destructive">*</span></Label>
              <select
                id="eci-section"
                value={form.section}
                onChange={(e) => setForm((f) => ({ ...f, section: e.target.value }))}
                className={FORM_SELECT_CLASS}
              >
                {CANONICAL_SECTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Validation possible</Label>
                <p className="text-xs text-muted-foreground">Off: a bill line mapped here is carried through, not checked.</p>
              </div>
              <Switch
                checked={form.validation_possible}
                onCheckedChange={(v) => setForm((f) => ({ ...f, validation_possible: v }))}
              />
            </div>
            {editing && (editing.usage_count ?? 0) > 0 && (
              <p className="text-xs text-muted-foreground">
                Renaming carries its {editing.usage_count} mapping{editing.usage_count === 1 ? '' : 's'} with it.
              </p>
            )}
            {formError && <Alert variant="destructive"><AlertDescription>{formError}</AlertDescription></Alert>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={() => void handleSave()} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dependencies */}
      <Dialog open={!!usagesFor} onOpenChange={(v) => { if (!v) setUsagesFor(null); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Mappings using “{usagesFor?.name}”</DialogTitle>
            <DialogDescription>
              {usages.length > 0
                ? 'This item cannot be deleted while these mappings point at it. Repoint or delete them first.'
                : 'No mapping uses this item.'}
            </DialogDescription>
          </DialogHeader>
          {usagesLoading ? (
            <div className="flex justify-center p-6"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : usagesError ? (
            <Alert variant="destructive"><AlertDescription>{usagesError}</AlertDescription></Alert>
          ) : usages.length > 0 && (
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Retailer</TableHead>
                    <TableHead>Distributor</TableHead>
                    <TableHead>Tariff</TableHead>
                    <TableHead>Bill section</TableHead>
                    <TableHead>Bill item</TableHead>
                    <TableHead>Map state</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {usages.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell>{u.retailer_name ?? '—'}</TableCell>
                      <TableCell>{u.distributor ?? '—'}</TableCell>
                      <TableCell className="font-mono text-sm">{u.tariff_code ?? '—'}</TableCell>
                      <TableCell className="text-sm">{u.bill_section}</TableCell>
                      <TableCell>{u.bill_item}</TableCell>
                      <TableCell className="text-sm">{u.map_state}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <DialogFooter>
            {usagesFor && usages.length > 0 && (
              <Button
                variant="outline" className="gap-1.5"
                onClick={() => { const name = usagesFor.name; setUsagesFor(null); onShowMappings(name); }}
              >
                <Link2 className="h-4 w-4" /> Open in Mappings
              </Button>
            )}
            <Button onClick={() => setUsagesFor(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete (unused items only) */}
      <Dialog open={!!deleting} onOpenChange={(v) => { if (!v && !deleteBusy) setDeleting(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete “{deleting?.name}”?</DialogTitle>
            <DialogDescription>No mapping uses this item. It is removed from the list.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)} disabled={deleteBusy}>Cancel</Button>
            <Button variant="destructive" onClick={() => void handleDelete()} disabled={deleteBusy}>
              {deleteBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
